import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import React from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as DeleteIcon } from '@assets/svg/users/ic-delete-user.svg';
import { ReactComponent as DownloadIcon } from '@assets/svg/users/ic-download-log.svg';
import { ReactComponent as EditIcon } from '@assets/svg/users/ic-edit-user.svg';
import { ReactComponent as ResendEmailIcon } from '@assets/svg/users/ic-resend-email.svg';
import { ReactComponent as ResetPasswordIcon } from '@assets/svg/users/ic-reset-password.svg';
import { ReactComponent as UnblockIcon } from '@assets/svg/users/ic-unblock-user.svg';

import { MODAL_NAMES } from '@constants/modalRenderers';
import { USER_STATUSES } from '@constants/userStatuses';

import { getUserAccessAndRole } from '@helpers/getUserAccessAndRole';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserDeleteMutation } from '@services/user/useUserDeleteMutation';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { useUserActivityLogMutation } from '../../services/useUserActivityLogMutation';
import { useUserResendConfirmMutation } from '../../services/useUserResendConfirmMutation';
import { useUserUnblockMutation } from '../../services/useUserUnblockMutation';
import UserModalHead from '../UserModalHead/UserModalHead';
import style from './style.module.scss';

const ContextMenu = ({ id }) => {
  const { t } = useTranslation();

  const { setActiveModal } = useModalActions();

  const { data: profileData } = useUserProfileQuery();
  const { mutate: userActivityLogMutate } = useUserActivityLogMutation();
  const { mutate: userResendConfirmMutate } = useUserResendConfirmMutation();
  const { mutate: userDeleteMutate } = useUserDeleteMutation();
  const { mutate: userUnblockMutate } = useUserUnblockMutation();

  const saveUserLog = (userId, userFullName) => {
    const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

    showToast({ variant: 'success', message: t('The download will begin shortly.') });

    userActivityLogMutate(
      { userId },
      {
        onSuccess: (data) => {
          saveAs(
            new Blob([data?.data], { type: 'text/plain;charset=utf-8' }),
            `${userFullName} ${format(new Date(), currentDateFormat)}.csv`,
          );
        },
      },
    );
  };

  const onSuccessAction = (result, successMessage) => {
    const resultStatus = result.data?.status === 'ok';
    showToast({
      variant: resultStatus ? 'success' : 'error',
      message: resultStatus ? t(successMessage) : result.data.message,
    });
  };

  const handleDeleteUser = (user) => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Delete User'),
        bodyContent: (
          <>
            {t('Are you sure you want to delete this user?')}
            <UserModalHead user={user} />
          </>
        ),
        confirmText: t('Delete'),
        onConfirm: () => {
          userDeleteMutate(
            { id: user.id },
            {
              onSuccess: (result) => onSuccessAction(result, 'User successfully deleted'),
            },
          );
        },
      },
    });
  };

  const handleResentEmail = (user) => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: (
          <>
            {t('Re-send')} {t('Welcome email')}
          </>
        ),
        bodyContent: (
          <>
            {t('Are you sure you want to send welcome email for this user?')}
            <UserModalHead user={user} />
          </>
        ),
        confirmText: t('Send'),
        onConfirm: () =>
          userResendConfirmMutate(
            { id: user.id },
            {
              onSuccess: (result) =>
                onSuccessAction(result, 'An account activation email has been sent to the selected user.'),
            },
          ),
      },
    });
  };

  const handleUnblockUser = (user) => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Unlock user account'),
        bodyContent: (
          <>
            {t('You are about to unlock the account for:')}
            <UserModalHead user={user} />
            <div className={style.unblockExtraText}>
              <div>{t('This account is currently locked due to multiple failed login attempts.')}</div>
              <div>
                {t(
                  'By confirming, you will unlock the account and reset the login attempt counter. An email notification will be sent to the user.',
                )}
              </div>
            </div>
          </>
        ),
        confirmText: t('Unlock account'),
        onConfirm: () =>
          userUnblockMutate(
            { userId: user.id },
            {
              onSuccess: (result) =>
                onSuccessAction(result, 'Unlock request submitted. The user will be notified by email.'),
            },
          ),
      },
    });
  };

  const getCanEdit = (row) => {
    if (profileData.role === 'Manager') {
      const { userRole: rowRole } = getUserAccessAndRole(row.permissions);
      return rowRole !== 'Super Admin' && rowRole !== 'Administrator';
    }
    return true;
  };

  return (
    <Tooltip
      id={id}
      place="left"
      openOnClick
      className={style.actionsColumnPopover}
      clickable
      disableStyleInjection
      noArrow
      render={({ content }) => {
        if (!content) {
          return null;
        }
        const row = JSON.parse(content);
        const canEdit = getCanEdit(row);

        return (
          <>
            {canEdit && (
              <div
                className={style.actionsColumnPopoverItem}
                onClick={() => setActiveModal({ name: MODAL_NAMES.userModal, options: { userId: row.id } })}
              >
                <EditIcon className={style.editIcon} />
                {t('Edit User')}
              </div>
            )}
            <div className={style.actionsColumnPopoverItem} onClick={() => saveUserLog(row.id, row.fullName)}>
              <DownloadIcon className={style.downloadIcon} />
              {t('User Log')}
            </div>
            {row.status === USER_STATUSES.UNCONFIRMED && (
              <div className={style.actionsColumnPopoverItem} onClick={() => handleResentEmail(row)}>
                <ResendEmailIcon className={style.resendIcon} />
                {t('Resend Welcome Email')}
              </div>
            )}
            {row.status === USER_STATUSES.ACTIVE && !row.main_customer_identity_provider && (
              <div
                className={style.actionsColumnPopoverItem}
                onClick={() =>
                  setActiveModal({
                    name: MODAL_NAMES.resetPasswordModal,
                    options: { userInfo: row },
                  })
                }
              >
                <ResetPasswordIcon className={style.resetIcon} />
                {t('Reset Password')}
              </div>
            )}
            {canEdit && (
              <div className={style.actionsColumnPopoverItem} onClick={() => handleDeleteUser(row)}>
                <DeleteIcon className={style.deleteIcon} />
                {t('Delete User')}
              </div>
            )}
            {row.is_blocked && (
              <div className={style.actionsColumnPopoverItem} onClick={() => handleUnblockUser(row)}>
                <UnblockIcon className={style.unblockIcon} />
                {t('Unblock User')}
              </div>
            )}
          </>
        );
      }}
    />
  );
};

export default ContextMenu;
